import { useState } from 'react';
import IconAdd from '../../../components/icons/IconAdd';
import IconDelete from '../../../components/icons/IconDelete';
import RichTextEditor from '../../../components/RichTextEditor';
import { renderRichText } from '../../../utils/richText';
import { safeParseConfig } from '../blockConfig';
import { resolveCorrectPositions, compactItems } from '../orderingConfig';

const DEFAULT = { prompt: '', items: ['', ''], correctPositions: [1, 2] };

// Each item carries an explicit correct number (1..N) - the student gets the
// items shuffled and drags them into numbered slots. Picking a number that
// another row already has swaps the two, so the numbers are always a valid
// permutation and never need a separate "fix duplicates" step.
export default function OrderingEditor({ block, onSave, onCancel }) {
   const initial = safeParseConfig(block.config, DEFAULT);
   const initialItems = initial.items && initial.items.length ? initial.items : ['', ''];
   const [prompt, setPrompt] = useState(initial.prompt || '');
   const [items, setItems] = useState(initialItems);
   const [positions, setPositions] = useState(() => resolveCorrectPositions(initialItems, initial.correctPositions));

   const updateItem = (i, value) => setItems(prev => prev.map((it, idx) => idx === i ? value : it));
   const addItem = () => {
      setItems(prev => [...prev, '']);
      setPositions(prev => [...prev, prev.length + 1]);
   };
   const removeItem = (i) => {
      const removed = positions[i];
      setItems(prev => prev.filter((_, idx) => idx !== i));
      setPositions(prev => prev.filter((_, idx) => idx !== i).map(p => (p > removed ? p - 1 : p)));
   };
   const setPosition = (i, next) => setPositions(prev => {
      const j = prev.findIndex((p, idx) => idx !== i && p === next);
      const copy = [...prev];
      if (j !== -1) copy[j] = prev[i];
      copy[i] = next;
      return copy;
   });

   const handleSave = () => {
      const compact = compactItems(items, positions);
      onSave({ text: prompt, config: JSON.stringify({ prompt, items: compact.items, correctPositions: compact.correctPositions }) });
   };

   return (
      <div className='exercise-block-ordering-editor'>
         <RichTextEditor rows={1} value={prompt} placeholder='Завдання (напр. Розташуй речення по порядку)' onChange={setPrompt} />
         <p className='exercise-block-hint-text'>Оберіть правильний номер для кожного елемента. Студент побачить їх у випадковому порядку й перетягуватиме на позиції 1, 2, 3…</p>
         {items.map((item, i) => (
            <div className='exercise-block-list-item-row' key={i}>
               <select className='exercise-block-order-position' value={positions[i]} onChange={e => setPosition(i, Number(e.target.value))} title='Правильний номер'>
                  {items.map((_, n) => <option key={n} value={n + 1}>{n + 1}</option>)}
               </select>
               <RichTextEditor rows={1} value={item} placeholder={'Елемент ' + (i + 1)} onChange={value => updateItem(i, value)} />
               <div className='exercise-block-item-remove-btn' onClick={() => removeItem(i)}><IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} /></div>
            </div>
         ))}
         <div className='exercise-block-item-add-btn' onClick={addItem}><IconAdd /> Додати елемент</div>
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={handleSave}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}

// Teacher-facing answer key: items listed in their CORRECT order with the
// number shown, not in authoring order.
export function renderOrderingPreview(block) {
   const cfg = safeParseConfig(block.config, DEFAULT);
   const items = (cfg.items || []).filter(it => it && it.trim());
   if(items.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;
   const positions = resolveCorrectPositions(cfg.items || [], cfg.correctPositions);
   const sorted = (cfg.items || []).map((text, i) => ({ text, pos: positions[i] })).filter(o => o.text && o.text.trim()).sort((a, b) => a.pos - b.pos);
   return (
      <div className='exercise-block-ordering-preview'>
         {cfg.prompt ? <p className='exercise-block-prompt-text'>{renderRichText(cfg.prompt)}</p> : ''}
         <ol className='exercise-block-list-preview'>{sorted.map((o, i) => <li key={i}>{renderRichText(o.text)}</li>)}</ol>
      </div>
   );
}
